import { Sun, Contrast, Droplets, Thermometer } from 'lucide-react';
import { EditorSlider } from './EditorSlider';
import { DEFAULT_ADJUSTMENTS, type AdjustmentState } from './imageEditorTypes';
import { useTranslation } from '../../i18n';
import styles from './ImageEditor.module.css';

interface AdjustControlsProps {
  adjustments: AdjustmentState;
  onChange: (adj: AdjustmentState) => void;
  onResetAll: () => void;
  onDragStart: () => void;
  onDragEnd: () => void;
}

export function AdjustControls({
  adjustments,
  onChange,
  onResetAll,
  onDragStart,
  onDragEnd,
}: AdjustControlsProps) {
  const { t } = useTranslation();

  function update(key: keyof AdjustmentState, value: number) {
    onChange({ ...adjustments, [key]: value });
  }

  const isDefault =
    adjustments.brightness === 0 &&
    adjustments.contrast === 0 &&
    adjustments.saturation === 0 &&
    adjustments.temperature === 0;

  return (
    <div className={styles.tabContent}>
      <EditorSlider
        icon={<Sun size={16} />}
        label={t('imageEditor.brightness')}
        min={-100}
        max={100}
        value={adjustments.brightness}
        defaultValue={DEFAULT_ADJUSTMENTS.brightness}
        formatValue={(v) => (v > 0 ? `+${v}` : String(v))}
        onChange={(v) => update('brightness', v)}
        onPointerDown={onDragStart}
        onPointerUp={() => onDragEnd()}
      />
      <EditorSlider
        icon={<Contrast size={16} />}
        label={t('imageEditor.contrast')}
        min={-100}
        max={100}
        value={adjustments.contrast}
        defaultValue={DEFAULT_ADJUSTMENTS.contrast}
        formatValue={(v) => (v > 0 ? `+${v}` : String(v))}
        onChange={(v) => update('contrast', v)}
        onPointerDown={onDragStart}
        onPointerUp={() => onDragEnd()}
      />
      <EditorSlider
        icon={<Droplets size={16} />}
        label={t('imageEditor.saturation')}
        min={-100}
        max={100}
        value={adjustments.saturation}
        defaultValue={DEFAULT_ADJUSTMENTS.saturation}
        formatValue={(v) => (v > 0 ? `+${v}` : String(v))}
        onChange={(v) => update('saturation', v)}
        onPointerDown={onDragStart}
        onPointerUp={() => onDragEnd()}
      />
      <EditorSlider
        icon={<Thermometer size={16} />}
        label={t('imageEditor.temperature')}
        min={-100}
        max={100}
        value={adjustments.temperature}
        defaultValue={DEFAULT_ADJUSTMENTS.temperature}
        formatValue={(v) => (v > 0 ? `+${v}` : String(v))}
        onChange={(v) => update('temperature', v)}
        onPointerDown={onDragStart}
        onPointerUp={() => onDragEnd()}
      />

      {!isDefault && (
        <button
          type="button"
          className={styles.resetAllBtn}
          onClick={onResetAll}
        >
          {t('imageEditor.resetAll')}
        </button>
      )}
    </div>
  );
}
